import { useEffect, useRef, useState } from "react"

import { USER_NAME } from "../config/constants"
import { INITIAL_REVEAL_COUNT } from "../sortaballs-10-colors/use-incremental-replay"
import { type ChallengeData, completeChallenge } from "./api"
import { acceptFlowStorage } from "./battle-storage"
import { getSolvedTimestamp } from "./get-solved-timestamp"
import { PENALTY_FREE_MOVES, readMoveCount } from "./read-move-count"
import { useRestoreLevel } from "./use-restore-level"

export interface Battle {
  challenge: ChallengeData | null
  concede: ()=> void
  containerRef: React.RefObject<HTMLDivElement | null>
  lost: boolean
  pause: ()=> void
  paused: boolean
  penaltyCount: number
  remainingSeconds: number
  resume: ()=> void
  solved: boolean
  start: (challenge: ChallengeData)=> void
  stop: ()=> void
  togglePause: ()=> void
  undoCount: number
}

export function useBattle(happy: boolean): Battle {
  const { restore, snapshot } = useRestoreLevel()
  const stored = acceptFlowStorage.loadBattle()
  const initialStartedAt = stored
    ? stored.startedAt + (stored.pausedAt ? Date.now() - stored.pausedAt : 0)
    : 0
  const [challenge, setChallenge] = useState<ChallengeData | null>(
    stored?.challenge ?? null,
  )
  const [lost, setLost] = useState(stored?.lost ?? false)
  const [undoCount, setUndoCount] = useState(stored?.undoCount ?? 0)
  const [hintsUsed, setHintsUsed] = useState(stored?.hintsUsed ?? 0)
  const [penaltyCount, setPenaltyCount] = useState(0)
  const [paused, setPaused] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(()=> {
    if (!stored) return 0
    const elapsed = Math.floor((Date.now() - initialStartedAt) / 1000)
    return Math.max(
      (stored.challenge.time ?? 0) - elapsed - (stored.undoCount ?? 0),
      0,
    )
  })
  const [solved, setSolved] = useState(false)
  const startedAtRef = useRef(initialStartedAt)
  const undoRef = useRef(undoCount)
  const hintsRef = useRef(hintsUsed)
  const timeoutSentRef = useRef(stored?.lost ?? false)
  const pausedRef = useRef(false)
  const pausedAtRef = useRef(stored?.pausedAt ?? 0)
  const solvedRef = useRef(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const initialStampRef = useRef<string | undefined>(
    stored?.challenge ? getSolvedTimestamp(stored.challenge) : undefined,
  )

  // The player gives up: the opponent (creator) wins with no time recorded,
  // mirroring the timeout outcome, then the battle is cleared back to the home
  // screen.
  const concede = (): void=> {
    if (challenge === null || solvedRef.current || timeoutSentRef.current)
      return
    completeChallenge({
      accepted_challenge_time: null,
      hints_used: hintsRef.current,
      id: challenge.id,
      winner: challenge.created_by,
    })
    timeoutSentRef.current = true
    stop()
  }

  const pause = (): void=> {
    if (
      !challenge ||
      pausedRef.current ||
      solvedRef.current ||
      timeoutSentRef.current
    ) {
      return
    }
    pausedAtRef.current = Date.now()
    pausedRef.current = true
    setPaused(true)
    acceptFlowStorage.saveBattle({
      challenge,
      hintsUsed: hintsRef.current,
      lost: false,
      paused: true,
      pausedAt: pausedAtRef.current,
      startedAt: startedAtRef.current,
      undoCount: undoRef.current,
    })
  }

  const resume = (): void=> {
    if (!pausedRef.current) return
    const battleChallenge = challenge ?? stored?.challenge
    if (!battleChallenge) {
      pausedRef.current = false
      setPaused(false)
      return
    }
    startedAtRef.current += Date.now() - pausedAtRef.current
    pausedRef.current = false
    setPaused(false)
    acceptFlowStorage.saveBattle({
      challenge: battleChallenge,
      hintsUsed: hintsRef.current,
      lost: false,
      paused: false,
      startedAt: startedAtRef.current,
      undoCount: undoRef.current,
    })
  }

  const togglePause = (): void=> {
    if (pausedRef.current) {
      resume()
    } else {
      pause()
    }
  }

  const start = (next: ChallengeData): void=> {
    const startedAt = Date.now()
    startedAtRef.current = startedAt
    timeoutSentRef.current = false
    solvedRef.current = false
    undoRef.current = 0
    pausedRef.current = false
    pausedAtRef.current = 0
    initialStampRef.current = getSolvedTimestamp(next)
    snapshot(next.hard_mode)
    setChallenge(next)
    setLost(false)
    setSolved(false)
    setPaused(false)
    setUndoCount(0)
    setHintsUsed(0)
    setPenaltyCount(0)
    setRemainingSeconds(next.time ?? 0)
    acceptFlowStorage.saveBattle({
      challenge: next,
      hintsUsed: 0,
      lost: false,
      startedAt,
      undoCount: 0,
    })
  }

  const stop = (): void=> {
    restore()
    acceptFlowStorage.clearBattle()
    startedAtRef.current = 0
    timeoutSentRef.current = false
    solvedRef.current = false
    undoRef.current = 0
    pausedRef.current = false
    hintsRef.current = 0
    setChallenge(null)
    setLost(false)
    setSolved(false)
    setPaused(false)
    setUndoCount(0)
    setHintsUsed(0)
    setPenaltyCount(0)
    setRemainingSeconds(0)
  }

  // Count the embedded game's Undo clicks (capture phase: the buttons live
  // inside Sortaballs10ColorsApp / Sortaballs8ColorsApp, whose own handlers must still run).
  // Each undo costs 1s off the countdown.
  useEffect(()=> {
    const container = containerRef.current
    if (container === null || !challenge) return
    const onClick = (event: MouseEvent): void=> {
      // SAFETY: DOM event target is always an HTMLElement
      const button = (event.target as HTMLElement).closest("button")
      const title = button?.getAttribute("title") ?? ""
      if (title === "Undo") {
        // No penalty until the player has made enough moves — the undo just
        // rewinds a still-short attempt. When it applies, drop the countdown
        // by 1s immediately so the cost is visible at the moment of the press.
        if (readMoveCount(container) >= PENALTY_FREE_MOVES) {
          undoRef.current += 1
          setUndoCount(undoRef.current)
          setPenaltyCount(prev=> prev + 1)
          setRemainingSeconds(prev=> Math.max(prev - 1, 0))
        }
      } else if (title.startsWith("Show")) {
        // Each press reveals a batch of correct moves, not a single one. The
        // game's reveal logic shows INITIAL_REVEAL_COUNT (5) on the first press
        // then +1 per press, so `used_hints` must report the number of correct
        // moves revealed — not the number of button presses.
        hintsRef.current =
          hintsRef.current === 0 ? INITIAL_REVEAL_COUNT : hintsRef.current + 1
        setHintsUsed(hintsRef.current)
      }
    }
    container.addEventListener("click", onClick, true)
    return ()=> container.removeEventListener("click", onClick, true)
  }, [challenge, happy])

  useEffect(()=> {
    if (challenge === null || solved || paused) return
    const intervalId = setInterval(()=> {
      const elapsedSeconds = Math.floor(
        (Date.now() - startedAtRef.current) / 1000,
      )
      const stamp = getSolvedTimestamp(challenge)
      const playerSolved =
        stamp !== undefined &&
        stamp !== initialStampRef.current &&
        solvedRef.current === false
      if (playerSolved) {
        solvedRef.current = true
        // With no opponent bar the player wins by solving at all; otherwise
        // they win only if time remains on the countdown.
        const winner =
          challenge.time === null ||
          challenge.time - elapsedSeconds - undoRef.current > 0
            ? (localStorage.getItem(USER_NAME) ?? "")
            : challenge.created_by
        completeChallenge({
          accepted_challenge_time: elapsedSeconds + undoRef.current,
          hints_used: hintsRef.current,
          id: challenge.id,
          winner,
        })
        setSolved(true)
        acceptFlowStorage.clearBattle()
        return
      }

      // No opponent bar yet: the player just plays to solve — there is no
      // countdown to lose against, so skip the deadline logic entirely.
      if (challenge.time === null || timeoutSentRef.current) return

      const remaining = Math.max(
        challenge.time - elapsedSeconds - undoRef.current,
        0,
      )
      setRemainingSeconds(remaining)
      if (remaining <= 0) {
        completeChallenge({
          accepted_challenge_time: null,
          hints_used: hintsRef.current,
          id: challenge.id,
          winner: challenge.created_by,
        })
        timeoutSentRef.current = true
        setLost(true)
        acceptFlowStorage.saveBattle({
          challenge,
          hintsUsed: hintsRef.current,
          lost: true,
          startedAt: startedAtRef.current,
          undoCount: undoRef.current,
        })
      }
    }, 1000)
    return ()=> clearInterval(intervalId)
  }, [challenge, solved, paused])

  return {
    challenge,
    concede,
    containerRef,
    lost,
    pause,
    paused,
    penaltyCount,
    remainingSeconds,
    resume,
    solved,
    start,
    stop,
    togglePause,
    undoCount,
  }
}
